package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.Block
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.model.BlacklistedGuest
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminBlacklistScreen(
    onNavigateBack: () -> Unit = {}
) {
    val viewModel = koinViewModel<AdminBlacklistViewModel>()
    val blacklistedGuests by viewModel.blacklistedGuests.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    var searchQuery by remember { mutableStateOf("") }
    var showAddDialog by remember { mutableStateOf(false) }
    var guestToDelete by remember { mutableStateOf<BlacklistedGuest?>(null) }

    val listState = rememberLazyListState()

    val filteredList = remember(blacklistedGuests, searchQuery) {
        if (searchQuery.isBlank()) {
            blacklistedGuests
        } else {
            blacklistedGuests.filter {
                it.fullName.contains(searchQuery, ignoreCase = true) ||
                it.identityNo.contains(searchQuery, ignoreCase = true) ||
                it.reason.contains(searchQuery, ignoreCase = true)
            }
        }
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = onNavigateBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Müşteri Kara Liste Yönetimi"),
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "${blacklistedGuests.size} ${l10n("Kayıtlı Misafir Engellendi")}",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = { showAddDialog = true },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(6.dp))
                            Text(l10n("Kara Liste Ekle"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }

                        LanguageSelectorRow(isDarkTheme = false)
                    }
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScrollbar(listState)
        ) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                item {
                    OutlinedTextField(
                        value = searchQuery,
                        onValueChange = { searchQuery = it },
                        placeholder = { Text(l10n("Ad veya Kimlik No ile ara..."), color = Color(0xFF94A3B8), fontSize = 14.sp) },
                        leadingIcon = { Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B)) },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF0F172A),
                            unfocusedBorderColor = Color(0xFFE2E8F0),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White
                        )
                    )
                }

                if (isLoading) {
                    item {
                        LinearProgressIndicator(modifier = Modifier.fillMaxWidth(), color = Color(0xFFDC2626))
                    }
                }

                if (filteredList.isEmpty() && !isLoading) {
                    item {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(48.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Icon(Icons.Outlined.Block, contentDescription = null, tint = Color(0xFFCBD5E1), modifier = Modifier.size(56.dp))
                                Spacer(Modifier.height(12.dp))
                                Text(
                                    if (searchQuery.isNotBlank()) l10n("Arama kriterine uygun kara liste kaydı bulunamadı.")
                                    else l10n("Henüz kara listeye alınmış müşteri bulunmuyor."),
                                    color = Color(0xFF64748B),
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    }
                } else {
                    items(filteredList, key = { it.id }) { guest ->
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color.White),
                            elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
                            border = BorderStroke(1.dp, Color(0xFFFCA5A5)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(16.dp)) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Box(
                                            modifier = Modifier
                                                .size(44.dp)
                                                .clip(CircleShape)
                                                .background(Color(0xFFFEE2E2)),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Icon(
                                                Icons.Outlined.Block,
                                                contentDescription = null,
                                                tint = Color(0xFFDC2626),
                                                modifier = Modifier.size(22.dp)
                                            )
                                        }
                                        Spacer(Modifier.width(14.dp))
                                        Column {
                                            Text(
                                                text = guest.fullName,
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 15.sp,
                                                color = Color(0xFF0F172A)
                                            )
                                            Spacer(Modifier.height(2.dp))
                                            Text(
                                                text = if (guest.identityNo.isNotBlank()) "${l10n("TC / Pasaport No")}: ${guest.identityNo}" else l10n("Kimlik No Girilmemiş"),
                                                fontSize = 12.sp,
                                                color = Color(0xFF64748B)
                                            )
                                        }
                                    }

                                    IconButton(onClick = { guestToDelete = guest }) {
                                        Icon(Icons.Outlined.Delete, contentDescription = l10n("Sil"), tint = Color(0xFFDC2626))
                                    }
                                }

                                HorizontalDivider(modifier = Modifier.padding(vertical = 10.dp), color = Color(0xFFFEE2E2))

                                Row(verticalAlignment = Alignment.Top) {
                                    Icon(Icons.Outlined.Warning, contentDescription = null, tint = Color(0xFFDC2626), modifier = Modifier.size(16.dp))
                                    Spacer(Modifier.width(8.dp))
                                    Column {
                                        Text(l10n("Gerekçe / Nedeni:"), fontWeight = FontWeight.Bold, fontSize = 12.sp, color = Color(0xFF991B1B))
                                        Spacer(Modifier.height(2.dp))
                                        Text(
                                            text = guest.reason.ifBlank { l10n("Gerekçe belirtilmemiş.") },
                                            fontSize = 13.sp,
                                            color = Color(0xFF1E293B),
                                            lineHeight = 18.sp
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                item {
                    Spacer(Modifier.height(32.dp))
                }
            }
        }

        if (showAddDialog) {
            AddBlacklistDialog(
                onDismiss = { showAddDialog = false },
                onAdd = { fullName, identityNo, reason ->
                    viewModel.addBlacklistRecord(fullName, identityNo, reason)
                    showAddDialog = false
                }
            )
        }

        if (guestToDelete != null) {
            val guest = guestToDelete!!
            AlertDialog(
                onDismissRequest = { guestToDelete = null },
                containerColor = Color.White,
                shape = RoundedCornerShape(20.dp),
                title = { Text(l10n("Kara Liste Kaydını Sil"), fontWeight = FontWeight.Bold) },
                text = { Text("${guest.fullName} " + l10n("isimli misafiri kara listeden çıkarmak istediğinize emin misiniz?")) },
                confirmButton = {
                    Button(
                        onClick = {
                            viewModel.removeBlacklistRecord(guest.id)
                            guestToDelete = null
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(l10n("Evet, Çıkar"), fontWeight = FontWeight.Bold, color = Color.White)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { guestToDelete = null }) {
                        Text(l10n("İptal"), color = Color(0xFF64748B))
                    }
                }
            )
        }
    }
}

@Composable
private fun AddBlacklistDialog(
    onDismiss: () -> Unit,
    onAdd: (fullName: String, identityNo: String, reason: String) -> Unit
) {
    var fullName by remember { mutableStateOf("") }
    var identityNo by remember { mutableStateOf("") }
    var reason by remember { mutableStateOf("") }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.Block, contentDescription = null, tint = Color(0xFFDC2626))
                Spacer(Modifier.width(8.dp))
                Text(l10n("Yeni Kara Liste Kaydı"), fontWeight = FontWeight.Bold, fontSize = 18.sp)
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                OutlinedTextField(
                    value = fullName,
                    onValueChange = {
                        fullName = it
                        errorMessage = null
                    },
                    label = { Text(l10n("Müşteri Adı Soyadı *")) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    singleLine = true
                )

                OutlinedTextField(
                    value = identityNo,
                    onValueChange = { identityNo = it },
                    label = { Text(l10n("TC Kimlik / Pasaport No")) },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    singleLine = true
                )

                OutlinedTextField(
                    value = reason,
                    onValueChange = {
                        reason = it
                        errorMessage = null
                    },
                    label = { Text(l10n("Kara Listeye Alınma Gerekçesi *")) },
                    modifier = Modifier.fillMaxWidth().height(100.dp),
                    placeholder = { Text(l10n("Örn: Ödeme yapmadan kaçış, tesise zarar verme vb.")) },
                    shape = RoundedCornerShape(10.dp)
                )

                if (errorMessage != null) {
                    Text(errorMessage ?: "", color = Color(0xFFDC2626), fontSize = 12.sp)
                }
            }
        },
        confirmButton = {
            val emptyFieldError = l10n("Lütfen müşteri adı ve gerekçe belirtiniz.")
            Button(
                onClick = {
                    if (fullName.isBlank() || reason.isBlank()) {
                        errorMessage = emptyFieldError
                        return@Button
                    }
                    onAdd(fullName.trim(), identityNo.trim(), reason.trim())
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(l10n("Kaydet ve Engelle"), fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(l10n("İptal"), color = Color(0xFF64748B))
            }
        }
    )
}
